import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { chartMonthLabelFormatter } from "../../utils/dashboardChartUtils";

const ROW_HEIGHT = 22;
const HEADER_BLOCK = 36;
const BOX_PADDING = 20;

const DashboardChartTooltip = ({
  active,
  payload,
  label,
  viewBox,
  valueFormatter,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!active || !payload?.length) return null;

  const sorted = [...payload].sort(
    (a, b) => (Number(b.value) || 0) - (Number(a.value) || 0),
  );

  const header = chartMonthLabelFormatter(label);
  const format =
    valueFormatter ??
    ((value) =>
      Number(value).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));

  const maxBoxWidth = viewBox?.width
    ? Math.max(200, viewBox.width - 12)
    : isMobile
      ? 300
      : 360;
  const boxWidth = Math.min(isMobile ? 300 : 360, maxBoxWidth);
  const left = viewBox ? viewBox.x + (viewBox.width - boxWidth) / 2 : 0;

  const availableHeight = viewBox?.height
    ? Math.max(100, viewBox.height - 12)
    : isMobile
      ? 280
      : 420;

  const idealHeight =
    HEADER_BLOCK + BOX_PADDING + sorted.length * ROW_HEIGHT;
  const boxHeight = Math.min(idealHeight, availableHeight);
  const top = viewBox
    ? viewBox.y + Math.max(6, (viewBox.height - boxHeight) / 2)
    : 8;

  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: boxWidth,
        height: boxHeight,
        padding: "10px 12px",
        backgroundColor: alpha(theme.palette.background.paper, 0.98),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        boxShadow: String(theme.shadows[4]),
        color: theme.palette.text.primary,
        fontSize: 12,
        boxSizing: "border-box",
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <p
        style={{
          margin: "0 0 8px",
          fontWeight: 700,
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {header}
      </p>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {sorted.map((entry, i) => (
          <li
            key={`${entry.name}-${i}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "baseline",
              gap: 16,
              marginBottom: 4,
              minHeight: ROW_HEIGHT - 4,
            }}
          >
            <span
              style={{
                minWidth: 0,
                textAlign: "left",
                color: entry.color,
                lineHeight: 1.25,
              }}
            >
              {entry.name}
            </span>
            <span
              style={{
                textAlign: "right",
                color: entry.color,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {format(entry.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DashboardChartTooltip;
